import React from "react";
import { connect } from "react-redux";
import { toggleSubMenuSidebar } from "../../store/Page";
import { Link, withRouter } from "react-router-dom";
import api from "../../api";
import {
  NEXT_LAUNCHPAD_TARGETS,
  isNextLaunchpadEnabledForUser,
  isNextLaunchpadRefinementsEnabledForUser,
  openNextLaunchpad,
} from "../../util/nextLaunchpad";
import { selectIsPLGAccount } from "../../store/userSelectors";
import {
  PLG_HIDDEN_CONVERSATION_ITEMS,
  PLG_HIDDEN_SECTION_KEYS,
  PLG_HIDDEN_SETTINGS_TABS,
} from "../../config/menuConfig";

const AI_LAUNCHPAD_COMPONENT = "__next_ai_launchpad__";
const AI_RECEPTIONIST_REFINEMENTS_COMPONENT =
  "__next_ai_receptionist_refinements__";
const AI_LAUNCHPAD_MENU_ITEM = {
  title: "AI Launchpad",
  component: AI_LAUNCHPAD_COMPONENT,
  launchpadTarget: NEXT_LAUNCHPAD_TARGETS.STANDARD_LAUNCHPAD,
};
const AI_RECEPTIONIST_REFINEMENTS_MENU_ITEM = {
  title: "Review My Setup",
  component: AI_RECEPTIONIST_REFINEMENTS_COMPONENT,
  launchpadTarget: NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS,
};
const AI_LAUNCHPAD_BUTTON_STYLE = {
  appearance: "none",
  background: "transparent",
  border: 0,
  font: "inherit",
  width: "100%",
  paddingLeft: "25px",
};
const CONVERSATION_ROUTES = [
  "/live-chat",
  "/completed-chats",
  "/chat-setup",
  "/installation",
  "/installation-and-integration",
];
const NEXT_VOIP_PATH = "/voip";
const DASHBOARD_OVERVIEW_PATH = "/dashboard-overview";
const DASHBOARD_OVERVIEW_MENU_ITEM = {
  title: "Dashboard Overview",
  key: "dashboard-overview",
  icon: "fa fa-chart-line",
  component: DASHBOARD_OVERVIEW_PATH,
};

const isDashboardOverviewMenuItem = (item) => {
  const title = (item?.title || "").toLowerCase();
  return (
    item?.key === DASHBOARD_OVERVIEW_MENU_ITEM.key ||
    item?.component === DASHBOARD_OVERVIEW_PATH ||
    title === "dashboard overview"
  );
};

const isExternalHttpUrl = (value) => {
  if (!value) {
    return false;
  }

  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch (_) {
    return false;
  }
};

const shouldUseBrowserNavigation = (component) => {
  return component === NEXT_VOIP_PATH || isExternalHttpUrl(component);
};

const isNextLaunchpadMenuComponent = (component) => {
  return (
    component === AI_LAUNCHPAD_COMPONENT ||
    component === AI_RECEPTIONIST_REFINEMENTS_COMPONENT
  );
};

const getNextLaunchpadMenuItems = ({ user }) => {
  const menuItems = [];

  if (isNextLaunchpadEnabledForUser({ user })) {
    menuItems.push(AI_LAUNCHPAD_MENU_ITEM);
  }

  if (isNextLaunchpadRefinementsEnabledForUser({ user })) {
    menuItems.push(AI_RECEPTIONIST_REFINEMENTS_MENU_ITEM);
  }

  return menuItems;
};

const addAiLaunchpadMenuItems = ({ menu, user }) => {
  const launchpadMenuItems = getNextLaunchpadMenuItems({ user });

  if (launchpadMenuItems.length === 0) {
    return menu;
  }

  return menu.map((item) => {
    const title = (item.title || "").toLowerCase();
    const childElements = item.childElement || [];
    const hasConversationChild = childElements.some((child) =>
      CONVERSATION_ROUTES.includes(child.component)
    );
    const isConversationMenu =
      item.key === "conversation" ||
      title === "conversation" ||
      title === "conversations" ||
      hasConversationChild;

    if (!isConversationMenu || childElements.length === 0) {
      return item;
    }

    const nextLaunchpadMenuItems = launchpadMenuItems.filter(
      (menuItem) =>
        !childElements.some((child) => child.component === menuItem.component)
    );

    if (nextLaunchpadMenuItems.length === 0) {
      return item;
    }

    return {
      ...item,
      childElement: [...childElements, ...nextLaunchpadMenuItems],
    };
  });
};

// Filter menu items for PLG accounts (only called when isPLG is true)
const filterMenuForPLG = ({ menu }) => {
  return menu
    .filter((item) => !PLG_HIDDEN_SECTION_KEYS.includes(item.key))
    .map((item) => {
      if (!item.childElement || item.childElement.length === 0) return item;

      const filteredChildren = item.childElement.filter((child) => {
        if (PLG_HIDDEN_CONVERSATION_ITEMS.includes(child.component)) return false;
        if (typeof child.component === "string") {
          if (PLG_HIDDEN_SETTINGS_TABS.some((tab) => child.component.includes(tab))) return false;
        }
        return true;
      });

      return { ...item, childElement: filteredChildren };
    });
};

const addPlgDashboardOverviewMenu = ({ menu }) => {
  let updatedMenu = menu.map((item) => {
    if (!isDashboardOverviewMenuItem(item)) {
      return item;
    }

    return {
      ...item,
      ...DASHBOARD_OVERVIEW_MENU_ITEM,
      title: item.title || DASHBOARD_OVERVIEW_MENU_ITEM.title,
      icon: item.icon,
    };
  });

  const dashboardOverviewIndex = updatedMenu.findIndex(isDashboardOverviewMenuItem);

  if (dashboardOverviewIndex === -1) {
    return [DASHBOARD_OVERVIEW_MENU_ITEM, ...updatedMenu];
  }

  if (dashboardOverviewIndex > 0) {
    const [dashboardOverviewItem] = updatedMenu.splice(dashboardOverviewIndex, 1);
    updatedMenu = [dashboardOverviewItem, ...updatedMenu];
  }

  return updatedMenu;
};

class Sidebar extends React.Component {
  constructor(props) {
    // console.log("leftBar",props)
    super(props);

    // Debug: Check PLG account status
    console.log("=== SIDEBAR PLG CHECK ===");
    console.log("isPLG from Redux:", props.isPLG);
    console.log("========================");

    // Apply PLG filtering first, then add dashboard overview and AI launchpad links.
    let menu = props.isPLG
      ? addPlgDashboardOverviewMenu({
          menu: filterMenuForPLG({
            menu: props.user.leftBarAccessList,
          }),
        })
      : props.user.leftBarAccessList;
    menu = addAiLaunchpadMenuItems({
      menu: menu,
      user: props.user,
    });

    this.state = {
      subMenu: "",
      menu: menu,
      sidebar_color: props.user.customizedDashboard[0].sidebar_color,
      sidebar_icon_color: props.user.customizedDashboard[0].sidebar_icon_color,
      twilioStatus: false,
      nextLaunchpadLoadingTarget: "",
      //[
      //   {
      //     title: "Dashboard",
      //     key: "dashboard",
      //     icon: "yr-navicon-user",
      //   },
      //   {
      //     title: "Conversation",
      //     key: "conversation",
      //     icon: "yr-navicon-t-script",
      //     childElement: [
      //       { title: "Live Chat", component: "/live-chat" },
      //       { title: "Your Chats", component: "/completed-chats" },

      //       { title: "Chat Setup", component: "/chat-setup" },

      //       {
      //         title: "Installation and Integration",
      //         component: "/installation-and-integration",
      //       },
      //     ],
      //   },
      //   {
      //     title: "Review Management",
      //     key: "review",
      //     icon: "yr-navicon-review",
      //     childElement: [
      //       { title: "Your Reviews", component: "/your-reviews" },
      //       { title: "Team Activity", component: "/team-activity" },
      //       { title: "Request A Review", component: "/request-a-review" },

      //       { title: "Settings", component: "/settings/team-listing" },
      //     ],
      //   },
      //   {
      //     title: "Customers and Analytics",
      //     key: "analytics",
      //     icon: "yr-navicon-codeicon",
      //   },
      //   {
      //     title: "Automated Marketing",
      //     key: "marketing",
      //     icon: "yr-navicon-gareicon",
      //     childElement: [
      //       { title: "Email" },
      //       { title: "Outbound SMS" },
      //       { title: "Your Call List" },
      //       { title: "Configuration Setting" },
      //     ],
      //   },
      //   {
      //     title: "Skills and Workspaces",
      //     key: "workspaces",
      //     icon: "yr-navicon-bot-builder",
      //     childElement: [
      //       { title: "Legacy conversations", component: "/automation" },
      //       { title: "Workspaces", component: "/botbuilder" },
      //       { title: "Intent Engine" },
      //     ],
      //   },
      //   {
      //     title: "Settings",
      //     key: "settings",
      //     icon: "yr-navicon-gareicon",
      //     childElement: [
      //       { title: "Profile", component: "/settings?tab=profile" },
      //       { title: "Manage User", component: "/settings?tab=manageuser" },
      //       { title: "Sensitive Data Activity Log" },

      //       { title: "Support Ticket", component: "/settings?tab=support" },
      //     ],
      //   },
      // ],
    };
  }
  componentWillUnmount() {
    this.unlisten();
  }

  toggleSelectedMenu = (location) => {
    if (location.pathname === DASHBOARD_OVERVIEW_PATH) {
      const overviewItem = this.state.menu.find(
        (menu) => menu.key === DASHBOARD_OVERVIEW_MENU_ITEM.key
      );
      if (overviewItem) {
        this.setState({ subMenu: overviewItem.key });
        return;
      }
    }

    // console.log('selectedMenu 1',location);
    const selectedMenu = this.state.menu.find((menu) => {
      if (menu.component && menu.component.includes(location.pathname)) {
        return true;
      }

      if (!menu.childElement || menu.childElement.length === 0) {
        return false;
      }

      return menu.childElement.some(
        (subMenu) =>
          subMenu.component && subMenu.component.includes(location.pathname)
      );
    });
    // console.log('selectedMenu',selectedMenu);
    if (selectedMenu) {
      this.setState({ subMenu: selectedMenu.key });
    }
  };

  componentDidMount() {
    const { location } = this.props;
    this.unlisten = this.props.history.listen((location, action) => {
      document.querySelector("#kt_aside_close_btn").click();
    });
    if (location.pathname !== "") {
      this.toggleSelectedMenu(location);
    }
     this.checkTwilioStatus();
  }
  checkTwilioStatus = async () => {
  this.setState({ loading: true });
  try {
    const { user } = this.props;
    if (!user?.user_id) {
      console.warn("User ID missing for Twilio status check");
      this.setState({ twilioStatus: false });
      return;
    }

    const response = await api.trustHub.checkTwilioStatus(user.user_id);
    const hasTwilio = response?.data?.status === true;

    this.setState({ twilioStatus: hasTwilio });
  } catch (err) {
    console.error("Error fetching Twilio status:", err);
    this.setState({ twilioStatus: false });
  } finally {
    this.setState({ loading: false });
  }
};

  openLaunchpadTarget = async (event, target) => {
    event.preventDefault();

    const canOpenTarget =
      target === NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS
        ? isNextLaunchpadRefinementsEnabledForUser({ user: this.props.user })
        : isNextLaunchpadEnabledForUser({ user: this.props.user });

    if (!canOpenTarget) {
      return;
    }

    this.setState({ nextLaunchpadLoadingTarget: target });

    try {
      await openNextLaunchpad({ user: this.props.user, target });
    } catch (err) {
      console.error("Error opening AI Launchpad:", err);
      alert("AI Launchpad is not available right now. Please try again.");
    } finally {
      this.setState({ nextLaunchpadLoadingTarget: "" });
    }
  };


  toggleMenu = () => {
    let body = document.body;

    body.classList.remove("kt-aside--on");

    this.props.toggleSubMenuSidebar({ toggle: false });
  };
  toggleSubMenu = (subMenu, component) => {
    if (this.state.subMenu === subMenu) {
      subMenu = "";
    }
    if (component) {
      this.props.history.push(component);
    }
    this.setState({ subMenu });
  };

  displayChildElement = (item) => {
    const { twilioStatus } = this.state;
  // Filter out duplicate Trust Hub entries only (keep only first occurrence)
  const seenTrustHub = new Set();
  item = item.filter(child => {
    if (child.component === "/trust-hub" || (typeof child.component === 'string' && child.component.includes('trust-hub'))) {
      if (seenTrustHub.has("trust-hub")) {
        return false;
      }
      seenTrustHub.add("trust-hub");
    }
    return true;
  });
    return item.map((listItem) => {
      if (isNextLaunchpadMenuComponent(listItem.component)) {
        const isOpening =
          this.state.nextLaunchpadLoadingTarget === listItem.launchpadTarget;
        const openingLabel =
          listItem.launchpadTarget === NEXT_LAUNCHPAD_TARGETS.PARTNER_REFINEMENTS
            ? "Opening your setup..."
            : "Opening AI Launchpad...";

        return (
          <li
            key={listItem.component}
            className="kt-menu__item "
            aria-haspopup="true"
          >
            <button
              type="button"
              className="kt-menu__link"
              onClick={(event) =>
                this.openLaunchpadTarget(event, listItem.launchpadTarget)
              }
              onMouseOver={this.aClass}
              onMouseLeave={this.rClass}
              style={AI_LAUNCHPAD_BUTTON_STYLE}
            >
              <i className="kt-menu__link-bullet kt-menu__link-bullet--dot" >
                <span
                  style={{ backgroundColor: this.state.sidebar_icon_color }}
                  onMouseOver={this.aClass}
                  onMouseLeave={this.rClass}></span>
              </i>
              <span className="kt-menu__link-text" style={{ color: this.state.sidebar_icon_color }}>
                {isOpening ? openingLabel : listItem.title}
              </span>
            </button>
          </li>
        );
      }

      let linkTarget
      if (typeof listItem.component == "undefined") {
        linkTarget = listItem.component ? listItem.component : "#";
      } else if (listItem.component.includes("setting")) {
        linkTarget = listItem.component ? listItem.component : "#";
      } else {
        linkTarget = {
          pathname: listItem.component ? listItem.component : "#",
          key: Date.now(), // we could use Math.random, but that's not guaranteed unique.
          state: {
            applied: true
          }
        };
      }

      const useBrowserNavigation = shouldUseBrowserNavigation(listItem.component);

      const isHidden =
        (listItem.component === "/outbound-campaign" && !twilioStatus) ||
        (listItem.component === "/deep-call-analytics" && this.props.user.is_vocera === "No");

      return (
        <li
          key={listItem.component || listItem.title}
          className="kt-menu__item "
          aria-haspopup="true"
          style={{ display: isHidden ? "none" : "" }}
        >
          {useBrowserNavigation ? (
            <a
              className="kt-menu__link"
              href={listItem.component}
              onMouseOver={this.aClass}
              onMouseLeave={this.rClass}
            >
              <i className="kt-menu__link-bullet kt-menu__link-bullet--dot" >
                <span
                  style={{ backgroundColor: this.state.sidebar_icon_color }}
                  onMouseOver={this.aClass}
                  onMouseLeave={this.rClass}></span>
              </i>
              <span className="kt-menu__link-text" style={{ color: this.state.sidebar_icon_color }}>{listItem.title}</span>
            </a>
          ) : (
            <Link
              className="kt-menu__link"
              to={linkTarget}
              onMouseOver={this.aClass}
              onMouseLeave={this.rClass}
            >
              <i className="kt-menu__link-bullet kt-menu__link-bullet--dot" >
                <span
                  style={{ backgroundColor: this.state.sidebar_icon_color }}
                  onMouseOver={this.aClass}
                  onMouseLeave={this.rClass}></span>
              </i>
              <span className="kt-menu__link-text" style={{ color: this.state.sidebar_icon_color }}>{listItem.title}</span>
            </Link>
          )}
        </li>
      );
    });
  };

  displayMenu = () => {
    //alert(this.state.sidebar_icon_color);

    return this.state.menu.map((item) => {
      return (
        <li className="kt-menu__item  kt-menu__item-submenu " key={item.key || item.title} >
          <a
            className="kt-menu__link kt-menu__toggle "
            onClick={() => this.toggleSubMenu(item.key, item.component)}
            style={{
              backgroundColor: this.state.subMenu === item.key ? this.state.sidebar_icon_color + '4d' : "",

            }}
            onMouseOver={this.aClass}
            onMouseLeave={this.rClass}
          >
            <i className={`kt-menu__link-icon ${item.icon}`} style={{ color: this.state.sidebar_icon_color }}></i>
            <span className="kt-menu__link-text" style={{ color: this.state.sidebar_icon_color }}>{item.title}</span>
            {item.childElement && (
              <i className="kt-menu__ver-arrow chevron right icon" style={{ color: this.state.sidebar_icon_color }}></i>
            )}
          </a>

          {item.childElement && (
            <div
              className="kt-menu__submenu "
              style={{
                display: this.state.subMenu === item.key ? "block" : "none",
              }}
            >
              <span className="kt-menu__arrow"></span>
              <ul className="kt-menu__subnav">
                {this.displayChildElement(item.childElement, item.key)}
              </ul>
            </div>
          )}
        </li>
      );
    });
  };

  changeBG = () => {
    var e = this.state.sidebar_color;
    //element.style.color = 'blue';
    document.getElementById("kt_aside_close_btn").style.backgroundColor = e;
    // e.target.style.backgroundColor = this.state.sidebar_icon_color
    //this.setState({ this.state.sidebar_icon_color });
  }

  changeTxt(e) {
    // e.target.style.color = this.state.sidebar_icon
  }

  render() {
    const className = `kt-aside  kt-aside--fixed  kt-grid__item kt-grid kt-grid--desktop kt-grid--hor-desktop ${this.props.toggle ? "kt-aside--on" : ""
      }`;
    return (
      <React.Fragment>
        <button
          className="kt-aside-close b-close"
          id="kt_aside_close_btn"
          onClick={this.toggleMenu}
          style={{ backgroundColor: this.state.sidebar_icon_color }}
        >
          <i className="la la-close"
            style={{ color: this.state.sidebar_color }}
          />
        </button>
        <div className={className} id="kt_aside">
          {this.state.loading && <div className="ui loading form"></div>}
          <div
            className="kt-aside-menu-wrapper kt-grid__item kt-grid__item--fluid"
            id="kt_aside_menu_wrapper"
            style={{ backgroundColor: this.state.sidebar_color }}
          >
            <div
              id="kt_aside_menu"
              className="kt-aside-menu "
              data-ktmenu-vertical={1}
              data-ktmenu-scroll={1}
            >
              <ul className="kt-menu__nav ">{this.displayMenu()}</ul>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}
const mapStateToProps = (state) => {
  return {
    toggle: state.page.toggleSubMenuSidebar,
    isPLG: selectIsPLGAccount(state),  // Get PLG status directly from Redux!
  };
};
export default connect(mapStateToProps, { toggleSubMenuSidebar })(
  withRouter(Sidebar)
);
